<script setup lang="ts">
import { useToast, type Toast } from '@/composables/useToast';

const { toasts, remove } = useToast();

function getIcon(type: Toast['type']): string {
  switch (type) {
    case 'success':
      return 'bi-check-circle-fill';
    case 'error':
      return 'bi-exclamation-circle-fill';
    case 'warning':
      return 'bi-exclamation-triangle-fill';
    case 'info':
      return 'bi-info-circle-fill';
    default:
      return 'bi-bell-fill';
  }
}

function getTypeClass(type: Toast['type']): string {
  switch (type) {
    case 'success':
      return 'toast-success';
    case 'error':
      return 'toast-error';
    case 'warning':
      return 'toast-warning';
    case 'info':
      return 'toast-info';
    default:
      return '';
  }
}
</script>

<template>
  <Teleport to="body">
    <div class="toast-container" aria-live="polite" aria-atomic="true">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast-item"
          :class="getTypeClass(toast.type)"
          role="alert"
        >
          <div class="toast-icon">
            <i :class="['bi', getIcon(toast.type)]"></i>
          </div>
          <div class="toast-content">
            <div v-if="toast.title" class="toast-title">{{ toast.title }}</div>
            <div class="toast-message">{{ toast.message }}</div>
          </div>
          <button
            class="toast-close"
            @click="remove(toast.id)"
            aria-label="Close notification"
          >
            <i class="bi bi-x"></i>
          </button>
          <!-- Progress bar for auto-dismiss -->
          <div
            v-if="!toast.persistent && toast.duration > 0"
            class="toast-progress"
            :style="{ animationDuration: `${toast.duration}ms` }"
          ></div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-container {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 400px;
  width: 100%;
  pointer-events: none;
}

.toast-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 1px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
  position: relative;
  overflow: hidden;
  border-left: 4px solid #6c757d;
}

.toast-icon {
  flex-shrink: 0;
  font-size: 18px;
  line-height: 1;
  margin-top: 2px;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 2px;
  color: #212529;
}

.toast-message {
  font-size: 14px;
  color: #495057;
  word-wrap: break-word;
}

.toast-close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  border-radius: 4px;
  font-size: 18px;
  line-height: 1;
  transition: background-color 0.15s, color 0.15s;
  margin: -4px -8px -4px 0;
}

.toast-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #212529;
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: currentColor;
  opacity: 0.3;
  animation: toast-progress linear forwards;
  width: 100%;
}

@keyframes toast-progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* Type-specific styles */
.toast-success {
  border-left-color: #198754;
}

.toast-success .toast-icon {
  color: #198754;
}

.toast-error {
  border-left-color: #dc3545;
}

.toast-error .toast-icon {
  color: #dc3545;
}

.toast-warning {
  border-left-color: #ffc107;
}

.toast-warning .toast-icon {
  color: #e6a800;
}

.toast-info {
  border-left-color: #0d6efd;
}

.toast-info .toast-icon {
  color: #0d6efd;
}

/* Transition animations */
.toast-enter-active {
  animation: toast-in 0.3s ease-out;
}

.toast-leave-active {
  animation: toast-out 0.2s ease-in forwards;
}

.toast-move {
  transition: transform 0.3s ease;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes toast-out {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
}

/* Responsive */
@media (max-width: 480px) {
  .toast-container {
    left: 16px;
    right: 16px;
    max-width: none;
  }
}

/* Dark mode styles */
:global([data-bs-theme="dark"]) .toast-item {
  background: #1e293b;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 0 1px rgba(0, 0, 0, 0.3);
}

:global([data-bs-theme="dark"]) .toast-title {
  color: #f1f5f9;
}

:global([data-bs-theme="dark"]) .toast-message {
  color: #94a3b8;
}

:global([data-bs-theme="dark"]) .toast-close {
  color: #94a3b8;
}

:global([data-bs-theme="dark"]) .toast-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
}

:global([data-bs-theme="dark"]) .toast-success {
  border-left-color: #22c55e;
}

:global([data-bs-theme="dark"]) .toast-success .toast-icon {
  color: #22c55e;
}

:global([data-bs-theme="dark"]) .toast-error {
  border-left-color: #ef4444;
}

:global([data-bs-theme="dark"]) .toast-error .toast-icon {
  color: #ef4444;
}

:global([data-bs-theme="dark"]) .toast-warning {
  border-left-color: #eab308;
}

:global([data-bs-theme="dark"]) .toast-warning .toast-icon {
  color: #eab308;
}

:global([data-bs-theme="dark"]) .toast-info {
  border-left-color: #3b82f6;
}

:global([data-bs-theme="dark"]) .toast-info .toast-icon {
  color: #3b82f6;
}
</style>
